import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, PenLine, Calculator, FileText, Users, Briefcase, DollarSign, MessageSquare, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";


const Nav = () => (
  <nav className="sticky top-0 z-40 bg-[#F6F5F0]/90 backdrop-blur-md border-b border-black/10">
    <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
        <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
        <span className="font-serif text-2xl font-bold tracking-tight">RevisaPro</span>
      </Link>
      <div className="hidden md:flex items-center gap-8 text-sm">
        <a href="#features" className="hover:text-[#D9381E] transition-colors">Funcionalidades</a>
        <a href="#pricing" className="hover:text-[#D9381E] transition-colors">Planos</a>
        <a href="#about" className="hover:text-[#D9381E] transition-colors">Para quem é</a>
      </div>
      <div className="flex items-center gap-3">
        <Link to="/login" data-testid="nav-login">
          <Button variant="ghost" className="rounded-none hover:bg-transparent hover:text-[#D9381E]">Entrar</Button>
        </Link>
        <Link to="/cadastro" data-testid="nav-register">
          <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white font-medium">
            Começar grátis
          </Button>
        </Link>
      </div>
    </div>
  </nav>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-black/10 p-8 transition-transform hover:-translate-y-1" data-testid={`feature-${title}`}>
    <Icon className="w-7 h-7 text-[#D9381E] mb-5" strokeWidth={1.8} />
    <h3 className="font-serif text-2xl mb-2">{title}</h3>
    <p className="text-[#4A4A4A] leading-relaxed text-sm">{desc}</p>
  </div>
);

const Plan = ({ name, price, tag, features, featured, testid, planKey }) => {
  const { user } = useAuth();
  const target = user ? `/app/checkout?plan=${planKey}` : `/cadastro?plan=${planKey}`;

  return (
    <div
      className={`p-8 ${featured ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/10"}`}
      data-testid={testid}
    >
      {tag && <div className="font-mono text-xs uppercase tracking-widest mb-4 text-[#D9381E]">{tag}</div>}
      <h3 className="font-serif text-3xl mb-2">{name}</h3>
      <div className="flex items-baseline gap-1 mb-6 font-mono">
        <span className="text-sm">R$</span>
        <span className="text-5xl font-semibold">{price}</span>
        <span className="text-sm opacity-70">/mês</span>
      </div>
      <ul className="space-y-3 mb-8 text-sm">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-[#D9381E] shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={target}>
        <Button
          className={`w-full rounded-none font-medium ${
            featured ? "bg-[#D9381E] hover:bg-[#B82914] text-white"
            : "bg-transparent border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white"
          }`}
        >
          Assinar {name}
        </Button>
      </Link>
    </div>
  );
};


export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F6F5F0] text-[#1A1A1A]">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#D9381E] mb-6">
              Para revisores de texto · Brasil
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mb-6">
              Organize, precifique
              <br />
              e <em className="text-[#D9381E] not-italic font-normal">escale</em> sua
              <br />
              revisão.
            </h1>
            <p className="text-lg text-[#4A4A4A] max-w-xl mb-10 leading-relaxed">
              O primeiro SaaS pensado para quem vive de revisar textos. Calcule preços justos, envie propostas irresistíveis e gerencie clientes sem planilha.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/cadastro">
                <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white text-base px-8 py-6 shadow-editorial" data-testid="hero-cta-start">
                  Começar grátis <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#features">
                <Button variant="ghost" className="rounded-none text-base px-8 py-6 hover:bg-transparent hover:text-[#D9381E]">
                  Ver funcionalidades
                </Button>
              </a>
            </div>
            <div className="mt-8 font-mono text-xs text-[#8A8A8A]">
              7 dias grátis · Sem cartão · Cancele quando quiser
            </div>
          </div>

          <div className="md:col-span-5 fade-up delay-200">
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/3ed879af-d5ab-4eae-99e7-0d82202de1fa/images/7fc5f35a21d63fb4396455e767a1a69633a9e654c8c00300ef41e47fe7c08f25.png"
                alt="Caneta vermelha sobre papel"
                className="w-full aspect-[4/5] object-cover border border-black/10"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-black/10 p-6 shadow-editorial max-w-[240px]">
                <div className="font-mono text-xs uppercase text-[#8A8A8A]">Ticket médio</div>
                <div className="font-serif text-4xl mt-1">R$ 487</div>
                <div className="text-xs text-[#4A4A4A] mt-1">↑ 34% após RevisaPro</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-black/10 bg-[#1A1A1A] text-[#F6F5F0]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["+38%", "aumento de receita"],
            ["R$ 0,04", "por palavra sugerido"],
            ["3x", "mais propostas fechadas"],
            ["7 dias", "para ver resultado"],
          ].map(([v, l]) => (
            <div key={l}>
              <div className="font-serif text-4xl md:text-5xl">{v}</div>
              <div className="font-mono text-xs uppercase tracking-widest mt-2 text-[#D9381E]">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-4">Funcionalidades</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            Tudo que falta na rotina<br />de quem revisa textos.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Feature icon={Calculator} title="Calculadora Inteligente" desc="Precifique TCC, artigos, livros e e-books considerando complexidade, prazo e nível de revisão. Sugerimos o mínimo viável e o preço-alvo." />
          <Feature icon={FileText} title="Propostas com IA" desc="Gere propostas persuasivas em segundos com Claude Sonnet 4.5. Totalmente editáveis e prontas para enviar." />
          <Feature icon={Users} title="CRM Simplificado" desc="Acompanhe leads em um pipeline Kanban: novo, contato, proposta, negociação, fechado." />
          <Feature icon={Briefcase} title="Gestão de Projetos" desc="Prazos, status, notas e anexos de cada trabalho em um só lugar. Nunca mais perca um deadline." />
          <Feature icon={DollarSign} title="Financeiro Claro" desc="Faturamento, ticket médio, a receber e histórico. Saiba quanto você ganha de verdade." />
          <Feature icon={MessageSquare} title="Scripts Comerciais" desc="Mensagens prontas para primeiro contato, follow-up e negociação. Geradas por IA com seu tom." />
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-[#EFECE6] border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1758876017749-d1bd378e2577?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxmb2N1c2VkJTIwcGVyc29uJTIwd3JpdGluZ3xlbnwwfHx8fDE3Nzc1NjE3NzJ8MA&ixlib=rb-4.1.0&q=85"
              alt="Revisora trabalhando"
              className="w-full aspect-[4/5] object-cover border border-black/10"
            />
          </div>
          <div className="md:col-span-7">
            <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-4">Feito para você</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-6 leading-tight">
              Revisor acadêmico, freelancer ou professor?
            </h2>
            <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
              Você estudou português, linguística, literatura. Mas ninguém te ensinou a precificar justo, negociar com segurança ou fechar clientes. É aí que a gente entra.
            </p>
            <ul className="space-y-3 text-base">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 mt-1 text-[#D9381E] shrink-0" /><span>Pare de revisar por centavos para competir</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 mt-1 text-[#D9381E] shrink-0" /><span>Organize clientes sem depender do WhatsApp</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 mt-1 text-[#D9381E] shrink-0" /><span>Saiba seu faturamento real todo mês</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-4">Planos</div>
          <h2 className="font-serif text-4xl md:text-5xl">Invista em você, mensalmente.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <Plan name="Básico" price="87,00" planKey="basico" features={[
            "Calculadora inteligente",
            "Até 10 propostas/mês",
            "CRM básico (50 leads)",
            "Suporte por e-mail",
          ]} testid="plan-basic" />
          <Plan name="Pro" price="187,00" planKey="pro" tag="Mais popular" featured features={[
            "Tudo do Básico",
            "Propostas com IA ilimitadas",
            "Gestão de projetos + financeiro",
            "Scripts comerciais com IA",
            "Leads ilimitados",
          ]} testid="plan-pro" />
          <Plan name="Premium" price="197,00" planKey="premium" features={[
            "Tudo do Pro",
            "Templates avançados",
            "Relatórios detalhados",
            "Suporte prioritário",
            "Consultoria mensal 1:1",
          ]} testid="plan-premium" />

        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] text-[#F6F5F0]">
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl mb-6 leading-tight">
            Sua revisão merece ser<br />um <em className="text-[#D9381E] not-italic">negócio</em>.
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
            Teste 7 dias grátis.
          </p>
          <Link to="/cadastro">
            <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white px-10 py-6 text-base" data-testid="cta-bottom">
              Começar agora <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-black/10 py-8 text-center text-sm text-[#8A8A8A] font-mono">
        © {new Date().getFullYear()} RevisaPro · Feito com <span className="text-[#D9381E]">♦</span> no Brasil
      </footer>
    </div>
  );
}
